import React, { PropsWithChildren } from 'react'
import { toast } from '@workwhile/ui'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useUpdateRequirementOnPositionMutation } from 'queries/company'
import { usePositionDetailWorkflowsContextValue } from '../../../context/PositionDetailWorkflowProvider'

type EditRequirementFlowContextState = {
  state: {
    requirement: NonNullable<
      ReturnType<
        typeof usePositionDetailWorkflowsContextValue
      >['editRequirementFlow']['data']
    >['requirement']
    isPending: boolean
    isError: boolean
    errorMessage: string | undefined
  }
  actions: {
    submit: (mustHave: boolean) => void
    reset: () => void
  }
}

const [EditRequirementFlowContext, useEditRequirementFlowContextValue] =
  createContext<EditRequirementFlowContextState>({
    name: 'EditRequirementFlowContext',
  })

export const EditRequirementFlowProvider = ({
  children,
}: PropsWithChildren) => {
  const {
    editRequirementFlow: { isOpen, close, data: flowData },
  } = usePositionDetailWorkflowsContextValue()

  const { mutate, reset, isPending, isError, error } =
    useUpdateRequirementOnPositionMutation(flowData?.positionId)

  if (isOpen && !flowData) {
    throw new Error(
      'Unable to find edit requirement flow data. Please report this error to the engineering team.'
    )
  }

  if (!flowData) {
    return null
  }

  const value: EditRequirementFlowContextState = {
    state: {
      requirement: flowData.requirement,
      isPending,
      isError,
      errorMessage: parseErrorMessage(error),
    },
    actions: {
      submit: (mustHave: boolean) => {
        mutate(
          { requirementId: flowData.requirement.id, mustHave },
          {
            onSuccess: () => {
              toast.success('Requirement updated successfully', {
                position: 'top-center',
              })
              close()
            },
          }
        )
      },
      reset,
    },
  }

  return (
    <EditRequirementFlowContext.Provider value={value}>
      {children}
    </EditRequirementFlowContext.Provider>
  )
}

export { useEditRequirementFlowContextValue }
